import { useState } from 'react';

import { PixelButton } from '../components/PixelButton';
import { DEMO_QUESTION, DEMO_RUBRIC_ROWS } from '../content/demo';
import { track } from '../lib/analytics';
import { useInView } from '../lib/useInView';

type Step = 'idle' | 'answering' | 'hinting' | 'revealed';

export const AssessmentPreview = () => {
  const [step, setStep] = useState<Step>('idle');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { ref, inView } = useInView<HTMLElement>({ threshold: 0.2, once: true });

  const q = DEMO_QUESTION;
  const selected = q.options.find((o) => o.id === selectedId);
  const isCorrect = selected?.isCorrect ?? false;
  const feedback = isCorrect ? q.feedback.correct : q.feedback.incorrect;

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setStep('hinting');
  };

  const handleReset = () => {
    setSelectedId(null);
    setStep('idle');
  };

  return (
    <section
      ref={ref}
      className={`interactive-demo${inView ? ' demo--revealed' : ''}`}
      aria-labelledby="demo-title"
    >
      <div className="container">
        <div className="demo__wrapper tile">
          <div className="demo__steps" aria-hidden="true">
            {(['idle', 'answering', 'hinting', 'revealed'] as Step[]).map((s, i) => (
              <span
                key={s}
                className={[
                  'demo__step-dot',
                  step === s ? 'demo__step-dot--active' : '',
                  (['idle', 'answering', 'hinting', 'revealed'] as Step[]).indexOf(step) > i
                    ? 'demo__step-dot--done'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            ))}
          </div>

          {step === 'idle' && (
            <div className="demo__panel demo__panel--idle">
              <p className="eyebrow">{q.eyebrow}</p>
              <h2 id="demo-title" className="demo__heading">
                Don&apos;t paste the answer. Think first.
              </h2>
              <p className="demo__intro">
                Most tools hand you the solution. Prepzio asks you to reason first. Try a
                junior-level sample below — you can take a hint, but the answer is not free.
              </p>
              <PixelButton
                size="lg"
                className="demo__attract-cta"
                onClick={() => {
                  track('sample_question_start');
                  setStep('answering');
                }}
              >
                Try a sample question →
              </PixelButton>
            </div>
          )}

          {step === 'answering' && (
            <div className="demo__panel">
              <p className="eyebrow">{q.eyebrow}</p>
              <h2 id="demo-title" className="demo__heading">
                {q.title}
              </h2>
              <p className="demo__intro">{q.body}</p>

              <pre className="demo__code" aria-label="JavaScript code snippet">
                <code>{q.code}</code>
              </pre>

              <p className="demo__prompt">{q.prompt}</p>

              <div className="demo__mcq" role="group" aria-label="Answer options">
                {q.options.map((opt, i) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`demo__option${i === 0 ? ' demo__option--pulse' : ''}`}
                    onClick={() => handleSelect(opt.id)}
                  >
                    <span className="demo__option-key">{opt.id.toUpperCase()}</span>
                    <span className="demo__option-label">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'hinting' && selected && (
            <div className="demo__panel">
              <p className="eyebrow">{q.eyebrow}</p>

              <div
                className={[
                  'demo__answer-banner',
                  'demo__answer-banner--pop',
                  isCorrect ? 'demo__answer-banner--correct' : 'demo__answer-banner--wrong',
                ].join(' ')}
                aria-live="polite"
              >
                <span className="demo__answer-banner-icon" aria-hidden="true">
                  {isCorrect ? '✓' : '✗'}
                </span>
                <div>
                  <p className="demo__answer-banner-verdict">
                    {isCorrect ? 'Correct!' : 'Not quite.'}
                  </p>
                  <p className="demo__answer-banner-pick">
                    You picked:{' '}
                    <strong>
                      {selected.id.toUpperCase()}. {selected.label}
                    </strong>
                  </p>
                </div>
              </div>

              <pre className="demo__code" aria-label="JavaScript code snippet">
                <code>{q.code}</code>
              </pre>

              <div className="demo__hint-box">
                <p className="demo__hint-label">{q.hint.label}</p>
                <ol className="demo__hint-list">
                  {q.hint.lines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ol>
                <p className="demo__hint-note">
                  Notice: no direct answer. That&apos;s intentional. That&apos;s Prepzio.
                </p>
              </div>

              <PixelButton size="md" onClick={() => setStep('revealed')}>
                See what comes next →
              </PixelButton>
            </div>
          )}

          {step === 'revealed' && (
            <div className="demo__panel">
              <p className="eyebrow">{q.eyebrow}</p>
              <h2 id="demo-title" className="demo__heading">
                Here&apos;s what Prepzio would do next
              </h2>

              <div
                className={[
                  'demo__feedback',
                  'demo__feedback--pop',
                  isCorrect ? 'demo__feedback--correct' : 'demo__feedback--incorrect',
                ].join(' ')}
              >
                <p className="demo__feedback-lead">{feedback.lead}</p>
                <p className="demo__feedback-follow">{feedback.followUp}</p>
              </div>

              <div className="demo__score-preview">
                <p className="demo__score-label">Session progress preview</p>
                <ul className="demo__rubric-list" aria-label="Practice session progress">
                  {DEMO_RUBRIC_ROWS.map((row) => (
                    <li
                      key={row.label}
                      className={[
                        'demo__rubric-row',
                        row.done ? 'demo__rubric-row--done' : '',
                        'locked' in row && row.locked ? 'demo__rubric-row--locked' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className="demo__rubric-icon" aria-hidden="true">
                        {'locked' in row && row.locked ? '🔒' : row.done ? '✓' : '○'}
                      </span>
                      <span>{row.label}</span>
                      {'locked' in row && row.locked && (
                        <span className="demo__rubric-chip">in app</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="demo__cta-row">
                <div>
                  <p className="demo__cta-lead">Get your first real skill check when beta opens.</p>
                  <PixelButton
                    size="lg"
                    onClick={() => {
                      const target = document.getElementById('waitlist');
                      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setTimeout(() => {
                        const input =
                          target?.querySelector<HTMLInputElement>('input[type="email"]');
                        input?.focus();
                      }, 600);
                    }}
                  >
                    Join the waitlist →
                  </PixelButton>
                </div>
                <button type="button" className="demo__restart" onClick={handleReset}>
                  ↩ Try again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
